import { createContext, type ReactNode, type RefObject, useContext, useMemo, useRef, useState } from 'react';
import { useServerEffectEvent } from '../../../utils/use-server-effect-event.js';

interface ColumnCollection {
  columns: Record<string, ColumnData>;
  setColumns: (columns: Record<string, ColumnData>) => void;
}

interface ColumnData {
  number?: number;
  width?: string | number;
  contentAlignment: ColumnContentAlignment;
}

/** Represents the direction of content alignment within a table column. */
export type ColumnContentAlignment = 'start' | 'center' | 'end';

const ColumnCollectionContext = createContext<ColumnCollection | null>(null);

const EMPTY_OBJECT = {};

export function ColumnCollectionProvider({ children }: { children: ReactNode }) {
  // When column collection is updated, we want to trigger a re-render,
  // but we also want immediate access to the updated collection during SSR.
  // Hence why it is stored as both a ref and a state.
  const columnsRef = useRef<Record<string, ColumnData>>(EMPTY_OBJECT);
  const [_columns, _setColumns] = useState<Record<string, ColumnData>>(EMPTY_OBJECT);

  const collection: ColumnCollection = useMemo(
    () => ({
      get columns() {
        return columnsRef.current;
      },
      setColumns: (columns) => {
        columnsRef.current = columns;
        _setColumns(columns);
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [_columns],
  );

  return <ColumnCollectionContext.Provider value={collection}>{children}</ColumnCollectionContext.Provider>;
}

type ColumnRegistrator = (key: string, data: ColumnData) => () => void;

// eslint-disable-next-line react-refresh/only-export-components
export function useColumnRegistration(): ColumnRegistrator {
  const columnCollection = useContext(ColumnCollectionContext);
  if (!columnCollection) {
    throw new Error('Cannot use column registration outside of a table');
  }

  return useServerEffectEvent((key, data) => {
    columnCollection.setColumns({
      ...columnCollection.columns,
      [key]: data,
    });

    return () => {
      const { columns } = columnCollection;

      if (columns[key] === data) {
        // This is a temporary deletion before a context update, so this is fine
        delete columns[key];
        columnCollection.setColumns({ ...columns });
      }
    };
  });
}

// eslint-disable-next-line react-refresh/only-export-components
export function useColumnData(key: string): Readonly<ColumnData> | undefined {
  const columnCollection = useContext(ColumnCollectionContext);
  if (!columnCollection) {
    throw new Error('Cannot use column data outside of a table');
  }

  return columnCollection.columns[key];
}

export interface UseFullWidthCell {
  columnKey: string;
  colSpan: number;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useFullWidthCell(ref: RefObject<HTMLTableCellElement | null>): UseFullWidthCell {
  const columnCollection = useContext(ColumnCollectionContext);
  if (!columnCollection) {
    throw new Error('Cannot use full width cell outside of a table');
  }

  // Full width cell should have the same column key as the first column in the table, so find it
  const parentTableElement = ref.current?.closest('table');
  const firstColumn = parentTableElement?.querySelector('[data-cell-row-key][data-cell-column-key]');

  return {
    columnKey: (firstColumn as HTMLTableCellElement | undefined)?.dataset.cellColumnKey ?? '???',
    colSpan: Object.keys(columnCollection.columns).length,
  };
}
